import { AliasForm } from "@/components/dashboard/integrations/tiktok/alias-form";
import { OrderSyncCard } from "@/components/dashboard/integrations/tiktok/order-sync-card";
import { ProductFetchForm } from "@/components/dashboard/integrations/tiktok/product-fetch-form";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import GatedOverlay from "@/components/ui/gated-overlay";
import { isGated } from "@/hooks/gated";
import { tiktokAccountQueryOpts } from "@/hooks/tiktok";
import { getOrgIdWithFallback } from "@/lib/utils";
import { useAuth } from "@/providers/auth";
import { useOrg } from "@/providers/organization";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute(
  "/$org/integrations/tiktok/$alias/account",
)({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    const { getToken, setToken, handleTokenInvalid } = context.auth;
    const orgId = getOrgIdWithFallback(context.org.orgId);

    const gated = await isGated(
      {
        token: getToken() as string,
        setToken,
        orgId: orgId,
      },
      handleTokenInvalid,
    );

    context.queryClient.ensureQueryData(
      tiktokAccountQueryOpts(params.alias, {
        setToken,
        token: getToken() as string,
        orgId: orgId,
      }),
    );

    return { gated: gated?.is_gated };
  },
});

function RouteComponent() {
  const { t } = useTranslation();
  const { gated } = Route.useLoaderData();
  const { org } = Route.useParams();
  const { alias } = Route.useParams();
  const { orgId } = useOrg();
  const resolvedOrgId = getOrgIdWithFallback(orgId);
  const { getToken, setToken } = useAuth();
  const { data: account } = useQuery(
    tiktokAccountQueryOpts(alias, {
      setToken,
      token: getToken() as string,
      orgId: resolvedOrgId,
    }),
  );

  if (gated) {
    return <GatedOverlay org={org} />;
  }

  return (
    <div>
      <div className="mx-auto max-w-3xl">
        {account && (
          <div className="space-y-2">
            <Card>
              <CardHeader>
                <CardTitle>{t("integrations.account.details")}</CardTitle>
              </CardHeader>

              <CardContent className="space-y-4">
                <AliasForm account={account} />
              </CardContent>

              <CardFooter className="flex justify-end">
                <Button
                  form="alias-form"
                  name="intent"
                  value={"details"}
                  type="submit"
                >
                  {t("common.save")}
                </Button>
              </CardFooter>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>{t("integrations.account.fetchTitle")}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col text-sm font-semibold">
                <span>{t("integrations.account.fetchDescription")}</span>
                <span className="text-xs text-gray-600">
                  {t("integrations.account.fetchHint")}
                </span>
              </CardContent>
              <CardFooter className="flex justify-end">
                <ProductFetchForm account={account} />
              </CardFooter>
            </Card>
            <OrderSyncCard
              tiktokPlatformConnectionId={account.platform_connection_id}
            />
          </div>
        )}
      </div>
    </div>
  );
}
